import { FolderKanban } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { projekteNachBereich, projektTitel, useCockpitProjekte } from "@/hooks/useCockpitProjekte";
import { cn } from "@/lib/utils";

const KEIN = "__kein__";

interface AuswahlProps {
  wert: string | null;
  onWert: (slug: string | null) => void;
  className?: string;
  /** Text für „kein Projekt“ (Dialog) bzw. „alle“ (Filter). */
  leerText?: string;
  /** Im Filter zusätzlich „Ohne Projekt“ anbieten. */
  ohneProjekt?: boolean;
}

/**
 * Auswahl eines Projekts aus Daniels Projekt-Cockpit, gruppiert nach Bereich, nur aktive.
 * Ein inaktives, schon gesetztes Projekt bleibt als Eintrag sichtbar.
 */
export function ProjektAuswahl({ wert, onWert, className, leerText = "Kein Projekt", ohneProjekt }: AuswahlProps) {
  const { data: projekte = [] } = useCockpitProjekte();
  const gruppen = projekteNachBereich(projekte);
  const inaktiv = wert && wert !== "__ohne__" ? projekte.find((p) => p.slug === wert && !p.aktiv) : undefined;

  return (
    <Select value={wert ?? KEIN} onValueChange={(w) => onWert(w === KEIN ? null : w)}>
      <SelectTrigger className={className}>
        <SelectValue placeholder="Projekt" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={KEIN}>{leerText}</SelectItem>
        {ohneProjekt && <SelectItem value="__ohne__">Ohne Projekt</SelectItem>}
        {inaktiv && <SelectItem value={inaktiv.slug}>{inaktiv.titel} (ruht)</SelectItem>}
        {gruppen.map((g) => (
          <SelectGroup key={g.bereich}>
            <SelectLabel>{g.bereich}</SelectLabel>
            {g.projekte.map((p) => (
              <SelectItem key={p.slug} value={p.slug}>
                {p.titel}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Kleine Pille mit dem Projektnamen (Karte und Liste). Ohne Projekt nichts. */
export function ProjektPille({ slug, className }: { slug: string | null | undefined; className?: string }) {
  const { data: projekte = [] } = useCockpitProjekte();
  const titel = projektTitel(projekte, slug);
  if (!titel) return null;
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 truncate rounded-full bg-[#E6F4F4] px-2 py-0.5 text-[11px] font-medium text-[#227277]",
        className,
      )}
      title={`Projekt: ${titel}`}
    >
      <FolderKanban className="h-3 w-3 shrink-0" />
      <span className="truncate">{titel}</span>
    </span>
  );
}
